import { apiService } from "@/services/apiService";
import { i18n } from "../main";
import { pickBy } from "lodash";
import { PERIOD } from "@/enums/period.enum";
import moment from "moment";
import { FORMAT } from "@/config/date-format";
import { getMostlySentiment } from "@/helpers/helpers";
const getChartData = (data, { date_to, date_from, fieldName }) => {
  if (!date_from && !date_to) {
    return data;
  }
  let index = 0;
  const result = [];
  const view = data[0] && PERIOD.MONTH in data[0] ? PERIOD.MONTH : PERIOD.WEEK;
  const dateStart = moment(date_from).startOf(view);
  const dateEnd = moment(date_to).startOf(view);
  const viewFormat = view === PERIOD.MONTH ? "M" : "W";
  while (dateStart <= dateEnd) {
    const currentDate = data.find(
      item => item[view] == dateStart.format(viewFormat)
    );

    result.push({
      [view]: +dateStart.startOf(view).format(viewFormat),
      year: +dateStart.format(FORMAT.YEAR),
      [fieldName]: currentDate ? currentDate[fieldName] : null,
      count: currentDate ? currentDate.count : null
    });
    dateStart.add(1, view);
    index++;
  }
  return result;
};

const getTotalSentimentNumber = sentiment => {
  return Object.values(sentiment).reduce(
    (total, { number }) => total + number,
    0
  );
};
const getSentimentScore = sentiment => {
  const totalCount = getTotalSentimentNumber(sentiment);
  const {
    positive: { number: positiveNumber },
    negative: { number: negativeNumber }
  } = sentiment;

  return Math.round(((positiveNumber - negativeNumber) / totalCount) * 100);
};

const getTableRowName = item => {
  if (item.facility_group || item.facility_group_name) {
    return item.facility_group || item.facility_group_name;
  }

  if (item.gmb_address || item.formatted_address) {
    return {
      gmbAddressName: item.name || (item.gmb_address && item.gmb_address.name),
      address:
        item.formatted_address ||
        (item.gmb_address && item.gmb_address.formatted_address)
    };
  }
  if (item.city) {
    return item.city;
  }

  return item.name;
};

export default class AnalyticsService {
  constructor(endpointCore) {
    this.endpointCore = endpointCore;
  }
  getHeatmap(params) {
    return apiService
      .get(this.endpointCore.heatmap, { params })
      .then(({ data }) =>
        data.map(
          ({ category_name: name, category_id: id, count, sentiment }) => ({
            id,
            name,
            count,
            ...sentiment,
            mostly: getMostlySentiment(sentiment),
            value: [
              getTotalSentimentNumber(sentiment),
              getSentimentScore(sentiment)
            ]
          })
        )
      );
  }

  getSummary(params) {
    return apiService
      .get(this.endpointCore.summary, { params })
      .then(({ data }) => {
        return {
          averageRatings: {
            range: data.period.count,
            value: data.period.average
          },
          total: {
            count: data.period.count,
            average: data.total.average
          }
        };
      });
  }

  getRatingsTable(params) {
    return apiService
      .get(this.endpointCore.ratingsTable, { params })
      .then(({ data }) => {
        return {
          count: data.count,
          results: data.results.map(item => {
            return { displayName: item.name, ...item };
          })
        };
      });
  }

  getAverageRatingsTable(params) {
    return apiService
      .get(this.endpointCore.averageRatingsTable, { params })
      .then(({ data: { results, count } }) => {
        const resultsPrepared = results.map(
          ({ id, name, average, average_total }) => {
            return {
              avg_review_period: average,
              avg_review_beginning: average_total,
              id,
              name,
              displayName: name
            };
          }
        );
        return { results: resultsPrepared, count };
      });
  }

  getRatingsPinnedTable(params) {
    return apiService
      .get(this.endpointCore.ratingsPinnedTable, { params })
      .then(
        ({
          data: {
            rating_1,
            rating_2,
            rating_3,
            rating_4,
            rating_5,
            rating_1_without_category,
            rating_2_without_category,
            rating_3_without_category,
            rating_4_without_category,
            rating_5_without_category
          }
        }) => {
          const ratings = { rating_1, rating_2, rating_3, rating_4, rating_5 };
          const ratingsWithoutCategory = {
            rating_1_without_category,
            rating_2_without_category,
            rating_3_without_category,
            rating_4_without_category,
            rating_5_without_category
          };
          const countRatings = Object.values(ratings).reduce((item, el) => {
            return item + el;
          }, 0);

          const countRatingsWithoutCategories = Object.values(
            ratingsWithoutCategory
          ).reduce((item, el) => {
            return item + el;
          }, 0);

          return [
            {
              name: i18n.t("message.common.allReviews"),
              count: countRatings,
              ...ratings
            },
            {
              name: i18n.t("message.common.reviewsWithoutCategory"),
              count: countRatingsWithoutCategories,
              rating_1: rating_1_without_category,
              rating_2: rating_2_without_category,
              rating_3: rating_3_without_category,
              rating_4: rating_4_without_category,
              rating_5: rating_5_without_category,
              withoutCategory: true
            }
          ];
        }
      );
  }
  getRatingsDetailsPinnedTable(params) {
    return apiService
      .get(this.endpointCore.ratingsAnalyticsDetailsTablePinned, { params })
      .then(
        ({ data: { rating_1, rating_2, rating_3, rating_4, rating_5 } }) => {
          const ratings = { rating_1, rating_2, rating_3, rating_4, rating_5 };

          const countRatings = Object.values(ratings).reduce((item, el) => {
            return item + el;
          }, 0);

          return [
            {
              name: i18n.t("message.common.allReviews"),
              count: countRatings,
              ...ratings,
              withoutCategory: false
            }
          ];
        }
      );
  }
  getAverageRatingsPinnedTable(params) {
    return apiService
      .get(this.endpointCore.averageRatingsPinnedTable, { params })
      .then(
        ({
          data: {
            average,
            average_total,
            average_without_category,
            average_without_category_total
          }
        }) => [
          {
            name: i18n.t("message.common.allReviews"),
            avg_review_period: average,
            avg_review_beginning: average_total
          },
          {
            name: i18n.t("message.common.reviewsWithoutCategory"),
            avg_review_period: average_without_category,
            avg_review_beginning: average_without_category_total,
            withoutCategory: true
          }
        ]
      );
  }
  getAverageRatingsDetailsPinnedTable(params) {
    return apiService
      .get(this.endpointCore.averageRatingsDetailsTablePinned, { params })
      .then(({ data: { average, average_total } }) => [
        {
          name: i18n.t("message.common.allReviews"),
          avg_review_period: average,
          avg_review_beginning: average_total,
          withoutCategory: false
        }
      ]);
  }
  getAverageRatingsDetailsChart(params) {
    return apiService
      .get(this.endpointCore.averageRatingsDetailsChart, { params })
      .then(({ data }) => data);
  }
  getAverageRatingsDetailsChartAll(params) {
    return apiService
      .get(this.endpointCore.averageRatingsDetailsChartAll, { params })
      .then(({ data }) => data);
  }
  getCountDetailsChart(params) {
    return apiService
      .get(this.endpointCore.countDetailsChart, { params })
      .then(({ data }) => data);
  }
  getCountDetailsChartAll(params) {
    return apiService
      .get(this.endpointCore.countDetailsChartAll, { params })
      .then(({ data }) => data);
  }
  getReviewsDetailsTable(params) {
    return apiService
      .get(this.endpointCore.ratingsDetailsTable, { params })
      .then(({ data }) => {
        const results = data.results.map(item => {
          return {
            name: getTableRowName(item),
            ...item
          };
        });
        return { count: data.count, results };
      });
  }
  getReviewsDetailsTableAll(params) {
    return apiService
      .get(this.endpointCore.ratingsAnalyticsDetailsTableAll, { params })
      .then(({ data: { results, count } }) => {
        const resultsPrepared = results.map(item => {
          return {
            name: getTableRowName(item),
            ...item
          };
        });
        return { count, results: resultsPrepared };
      });
  }
  getSentimentTable(params) {
    return apiService
      .get(this.endpointCore.sentimentTable, { params })
      .then(({ data: { results, count } }) => {
        const resultsPrepared = results.map(item => {
          return {
            ...item,
            name: getTableRowName(item)
          };
        });
        return { count, results: resultsPrepared };
      });
  }
  getSentimentAggregatedTable(params) {
    return apiService
      .get(this.endpointCore.sentimentAggregatedTable, {
        params
      })
      .then(({ data: { results, count } }) => {
        const resultsPrepared = results.map(item => {
          return {
            ...item,
            name: getTableRowName(item)
          };
        });

        return { count, results: resultsPrepared };
      });
  }
  getSentimentAggregatedDetailsChart(params) {
    return apiService
      .get(this.endpointCore.sentimentAggregatedDetailsChart, {
        params
      })
      .then(({ data }) => {
        return getChartData(data, params);
      });
  }
  getSentimentDetailsChart(params) {
    return apiService
      .get(this.endpointCore.sentimentDetailsChart, {
        params
      })
      .then(({ data }) => {
        return data;
      });
  }
  getAverageRatingsDetailsTable(params) {
    return apiService
      .get(this.endpointCore.averageAnalyticsDetailsTable, { params })
      .then(({ data }) => {
        const results = data.results.map(item => {
          return {
            name: getTableRowName(item),
            avg_review_period: item.average,
            avg_review_beginning: item.average_total,
            ...pickBy(item)
          };
        });
        return { count: data.count, results };
      });
  }
  getAverageRatingsDetailsTableAll(params) {
    return apiService
      .get(this.endpointCore.averageAnalyticsDetailsTableAll, { params })
      .then(({ data }) => {
        const results = data.results.map(item => {
          return {
            name: getTableRowName(item),
            avg_review_period: item.average,
            avg_review_beginning: item.average_total,
            ...pickBy(item)
          };
        });
        return { count: data.count, results };
      });
  }
  getCategorizationStatistics(params) {
    return apiService.get(this.endpointCore.categorizationStatistics, {
      params
    });
  }
  getCategorizationCompanySettings() {
    return apiService.get(this.endpointCore.categorizationCompanySettings);
  }
}
